import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScoreBar } from "@/components/Primitives";
import { ErrorNote } from "@/pages/Login";

const TYPE_LABEL = {
  mcq: "multiple choice",
  scenario: "scenario",
  short_answer: "short answer",
  self_assessment: "self assessment",
};

export default function Assessment() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/assessment/questions")
      .then(({ data }) => setQuestions(data.questions))
      .catch((e) => setError(errMsg(e, "Unable to load questions")))
      .finally(() => setLoading(false));
  }, []);

  const q = questions[idx];
  const answeredCount = useMemo(() => Object.keys(answers).filter((k) => answers[k] !== "" && answers[k] != null).length, [answers]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = { answers: Object.entries(answers).map(([questionKey, value]) => ({ questionKey, value })) };
      const { data } = await api.post("/assessment/submit", payload);
      setResult(data);
    } catch (e) {
      setError(errMsg(e, "Unable to submit assessment"));
    } finally {
      setBusy(false);
    }
  };

  const generateRoadmap = async () => {
    setBusy(true);
    try {
      await api.post("/roadmap/generate");
      navigate("/roadmap", { replace: true });
    } catch (e) {
      setError(errMsg(e, "Unable to generate roadmap"));
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  if (result)
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-14 fade-up" data-testid="assessment-result">
          <p className="overline text-primary">assessment complete</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">
            Overall score <span className="text-primary" data-testid="overall-score">{result.overallScore}</span>/100
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {result.answeredCount} of {result.totalQuestions} questions answered. Category scores drive your roadmap.
          </p>

          <div className="mt-10 border-t border-l border-border grid grid-cols-1 sm:grid-cols-2">
            {result.scores.map((s) => (
              <div key={s.category} data-testid={`score-${s.category}`} className="border-r border-b border-border p-5">
                <div className="flex items-baseline justify-between">
                  <p className="font-display font-bold tracking-tight">{s.categoryName}</p>
                  <p className="font-mono text-sm">{s.score}</p>
                </div>
                <div className="mt-3">
                  <ScoreBar value={s.score} />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{s.recommendedStartingPoint}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <p className="overline text-muted-foreground">strengths</p>
              <ul className="mt-3 space-y-1 text-sm" data-testid="result-strengths">
                {result.strengths.length ? result.strengths.map((s) => <li key={s} className="font-mono text-[hsl(var(--success))]">+ {s}</li>) : <li className="text-muted-foreground">None above 70 yet.</li>}
              </ul>
            </div>
            <div>
              <p className="overline text-muted-foreground">gaps</p>
              <ul className="mt-3 space-y-1 text-sm" data-testid="result-gaps">
                {result.gaps.length ? result.gaps.map((s) => <li key={s} className="font-mono text-destructive">- {s}</li>) : <li className="text-muted-foreground">No major gaps.</li>}
              </ul>
            </div>
          </div>

          <ErrorNote message={error} />
          <div className="mt-10 flex gap-3">
            <Button className="rounded-none gap-2" onClick={generateRoadmap} disabled={busy} data-testid="generate-roadmap-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Generate my roadmap <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => navigate("/dashboard")} data-testid="to-dashboard-btn">
              Go to dashboard
            </Button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen">
      <div className="border-b border-border sticky top-0 glass z-20">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <p className="overline text-primary">question {idx + 1} / {questions.length}</p>
            <p className="font-mono text-xs text-muted-foreground">{answeredCount} answered</p>
          </div>
          <div className="mt-4 h-1 bg-border">
            <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${((idx + 1) / questions.length) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="assessment-question">
        <ErrorNote message={error} />
        {q && (
          <div key={q.key} className="fade-up">
            <div className="flex gap-3 items-center">
              <span className="overline text-muted-foreground">{q.categoryName}</span>
              <span className="font-mono text-[10px] px-2 py-0.5 border border-border text-muted-foreground">{TYPE_LABEL[q.type]}</span>
            </div>
            <h2 className="mt-4 text-xl sm:text-2xl font-display font-bold tracking-tight leading-snug">{q.prompt}</h2>

            <div className="mt-8 space-y-px">
              {(q.type === "mcq" || q.type === "scenario") &&
                q.options.map((opt, i) => (
                  <button
                    key={i}
                    data-testid={`option-${i}`}
                    onClick={() => setAnswers({ ...answers, [q.key]: i })}
                    className={`w-full text-left border border-border p-4 text-sm transition-colors duration-150 ${
                      answers[q.key] === i ? "ring-1 ring-inset ring-primary bg-primary/5" : "hover:bg-accent/50"
                    }`}
                  >
                    <span className="font-mono text-xs text-primary mr-3">{String.fromCharCode(65 + i)}</span>
                    {opt}
                  </button>
                ))}

              {q.type === "short_answer" && (
                <Textarea
                  data-testid="short-answer-input"
                  rows={4}
                  className="rounded-none font-mono text-sm"
                  placeholder="Type your answer…"
                  value={answers[q.key] ?? ""}
                  onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                />
              )}

              {q.type === "self_assessment" && (
                <div className="grid grid-cols-5 gap-px bg-border border border-border">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      data-testid={`self-rating-${n}`}
                      onClick={() => setAnswers({ ...answers, [q.key]: n })}
                      className={`bg-background py-5 text-sm font-mono transition-colors duration-150 ${
                        answers[q.key] === n ? "ring-1 ring-inset ring-primary text-primary" : "hover:bg-accent/50"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <p className="col-span-5 bg-background px-3 py-2 text-xs text-muted-foreground">1 = never done it · 5 = I do this in production</p>
                </div>
              )}
            </div>

            <div className="mt-10 flex justify-between">
              <Button variant="ghost" className="rounded-none gap-2" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="prev-question">
                <ArrowLeft className="h-4 w-4" /> Previous
              </Button>
              {idx < questions.length - 1 ? (
                <Button className="rounded-none gap-2" onClick={() => setIdx(idx + 1)} data-testid="next-question">
                  Next <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button className="rounded-none gap-2" onClick={submit} disabled={busy} data-testid="submit-assessment">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit assessment
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
